import React from "react";
import {
  calculateCallBackTotalHours,
  calculateCallTotalHours,
  calculateRegularTotalHours,
  clockIntoTodayRow,
  fmtStatusTime,
  getHospitalDepartments
} from "../../utils/timecard";
import { useTimeFormat } from "../../hooks/useTimeFormat";
import { DAYS } from "../../constants/timecard";
import Field from "../common/Field";
import TimeCell from "../common/TimeCell";
import TimeFormatToggle from "../common/TimeFormatToggle";
import DepartmentSelect from "./DepartmentSelect";
import BreakApproverDialog from "./BreakApproverDialog";
import {
  getTodayRow,
  getTodayRowIndex,
  getRegularPhaseForRow,
  getCallPhase,
  getCallBackPhase,
  hasRegularShiftData,
  hasCallShiftData,
  hasCallBackShiftData,
  getShiftStatus,
  getTimeRangeSummary,
  getClockStatusText,
  SHIFT_LABELS,
  ACTIVE_PHASES
} from "../../utils/todayPhases";

// ── StatusPill ─────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  if (!status) return null;
  return (
    <span
      className={`today-status-pill today-status-pill--${status.variant}`}
      aria-label={`Status: ${status.label}`}
    >
      {status.label}
    </span>
  );
}

// ── Edit-mode field sections ───────────────────────────────────────────────

function RegularShiftFields({ row, rowIndex, hospitalOptions, onChange, onTookBreakChange, timeFormat }) {
  const [missedBreakPending, setMissedBreakPending] = React.useState(false);
  const [lateBreakPending, setLateBreakPending] = React.useState(false);

  const handleMissedBreakChange = (checked) => {
    const updatedRow = { ...row, tookBreak: checked };
    onTookBreakChange(rowIndex, { tookBreak: checked, totalHours: calculateRegularTotalHours(updatedRow, hospitalOptions) });
    if (checked) {
      setMissedBreakPending(true);
    }
  };

  const handleMissedBreakConfirm = (approverName) => {
    const existingNotes = row.notes || "";
    const approvalText = `Missed break approved by ${approverName}`;
    onChange(rowIndex, "notes", existingNotes ? `${existingNotes}\n${approvalText}` : approvalText);
    setMissedBreakPending(false);
  };

  const handleMissedBreakCancel = () => {
    if (!missedBreakPending) return;
    const updatedRow = { ...row, tookBreak: false };
    onTookBreakChange(rowIndex, { tookBreak: false, totalHours: calculateRegularTotalHours(updatedRow, hospitalOptions) });
    setMissedBreakPending(false);
  };

  const handleLateBreakChange = (checked) => {
    onChange(rowIndex, "lateBreak", checked);
    if (checked) {
      setLateBreakPending(true);
    }
  };

  const handleLateBreakConfirm = (approverName) => {
    const existingNotes = row.notes || "";
    const approvalText = `Late break approved by ${approverName}`;
    onChange(rowIndex, "notes", existingNotes ? `${existingNotes}\n${approvalText}` : approvalText);
    setLateBreakPending(false);
  };

  const handleLateBreakCancel = () => {
    if (!lateBreakPending) return;
    onChange(rowIndex, "lateBreak", false);
    setLateBreakPending(false);
  };

  return (
    <div className="today-shift-fields">

      {/* Hospital + Dept — compact metadata row */}
      <div className="tscard-field-section tscard-field-section--metadata">
        <div className="tscard-metadata-row">
          <Field id={`treg-${rowIndex}-hospital`} label="Hospital">
            <select id={`treg-${rowIndex}-hospital`} value={row.hospital}
              onChange={e => onChange(rowIndex, "hospital", e.target.value)}>
              <option value="">Select hospital</option>
              {hospitalOptions.map(h => (
                <option key={h.hospitalId} value={h.name}>{h.name}</option>
              ))}
            </select>
          </Field>
          <Field id={`treg-${rowIndex}-dept`} label="Dept">
            <DepartmentSelect
              id={`treg-${rowIndex}-dept`}
              ariaLabel="Department"
              departments={getHospitalDepartments(hospitalOptions, row.hospital)}
              value={row.department}
              onChange={value => onChange(rowIndex, "department", value)}
            />
          </Field>
        </div>
      </div>

      {/* Time tracking — 2×2 grid */}
      <div className="tscard-field-section">
        <span className="tscard-section-label">Time tracking</span>
        <div className="tscard-time-grid">
          <Field id={`treg-${rowIndex}-in`} label="Time in">
            <TimeCell id={`treg-${rowIndex}-in`} value={row.timeIn} format={timeFormat} ariaLabel="Time in"
              onChange={v => onChange(rowIndex, "timeIn", v)} />
          </Field>
          <Field id={`treg-${rowIndex}-bstart`} label="Break start">
            <TimeCell id={`treg-${rowIndex}-bstart`} value={row.breakStart} format={timeFormat} ariaLabel="Break start"
              onChange={v => onChange(rowIndex, "breakStart", v)} />
          </Field>
          <Field id={`treg-${rowIndex}-bend`} label="Break end">
            <TimeCell id={`treg-${rowIndex}-bend`} value={row.breakEnd} format={timeFormat} ariaLabel="Break end"
              onChange={v => onChange(rowIndex, "breakEnd", v)} />
          </Field>
          <Field id={`treg-${rowIndex}-out`} label="Time out">
            <TimeCell id={`treg-${rowIndex}-out`} value={row.timeOut} format={timeFormat} ariaLabel="Time out"
              onChange={v => onChange(rowIndex, "timeOut", v)} />
          </Field>
        </div>
      </div>

      {/* Compliance — inline row */}
      <div className="tscard-field-section">
        <div className="tscard-compliance-checks">
          {Number(row.totalHours) > 0 && (
            <span className="tscard-computed-inline">
              <span className="tscard-computed-label">Total</span>
              <strong className="tscard-computed-value">{row.totalHours} hrs</strong>
            </span>
          )}
          <label className="tscard-check-label" htmlFor={`treg-${rowIndex}-missed`}>
            <input id={`treg-${rowIndex}-missed`} type="checkbox" checked={row.tookBreak}
              onChange={e => handleMissedBreakChange(e.target.checked)} />
            <span>Missed break</span>
          </label>
          <label className="tscard-check-label" htmlFor={`treg-${rowIndex}-latebreak`}>
            <input id={`treg-${rowIndex}-latebreak`} type="checkbox" checked={row.lateBreak || false}
              onChange={e => handleLateBreakChange(e.target.checked)} />
            <span>Late break</span>
          </label>
          <label className="tscard-check-label" htmlFor={`treg-${rowIndex}-flexed`}>
            <input id={`treg-${rowIndex}-flexed`} type="checkbox" checked={row.flexed}
              onChange={e => onChange(rowIndex, "flexed", e.target.checked)} />
            <span>Flexed</span>
          </label>
          <label className="tscard-check-label" htmlFor={`treg-${rowIndex}-volflex`}>
            <input id={`treg-${rowIndex}-volflex`} type="checkbox" checked={row.volFlex}
              onChange={e => onChange(rowIndex, "volFlex", e.target.checked)} />
            <span>Voluntary Flex</span>
          </label>
        </div>
      </div>

      {/* Notes — full width */}
      <div className="tscard-field-section">
        <label className="tscard-notes-label" htmlFor={`treg-${rowIndex}-notes`}>Notes</label>
        <textarea
          id={`treg-${rowIndex}-notes`}
          className="tscard-notes-textarea"
          rows="2"
          value={row.notes}
          placeholder="Add notes about this shift..."
          onChange={e => onChange(rowIndex, "notes", e.target.value)}
          onInput={e => { e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }}
        />
      </div>
      <BreakApproverDialog
        open={missedBreakPending}
        title="Missed Break Approval"
        inputId="missed-break-approver-today"
        onConfirm={handleMissedBreakConfirm}
        onCancel={handleMissedBreakCancel}
      />
      <BreakApproverDialog
        open={lateBreakPending}
        title="Late Break Approval"
        inputId="late-break-approver-today"
        onConfirm={handleLateBreakConfirm}
        onCancel={handleLateBreakCancel}
      />
    </div>
  );
}

function CallShiftFields({ row, rowIndex, hospitalOptions, onChange, timeFormat }) {
  return (
    <div className="today-shift-fields">

      <div className="tscard-field-section tscard-field-section--metadata">
        <div className="tscard-metadata-row">
          <Field id={`tcall-${rowIndex}-hospital`} label="Hospital">
            <select id={`tcall-${rowIndex}-hospital`} value={row.hospital}
              onChange={e => onChange(rowIndex, "hospital", e.target.value)}>
              <option value="">Select hospital</option>
              {hospitalOptions.map(h => (
                <option key={h.hospitalId} value={h.name}>{h.name}</option>
              ))}
            </select>
          </Field>
          <Field id={`tcall-${rowIndex}-dept`} label="Dept">
            <DepartmentSelect
              id={`tcall-${rowIndex}-dept`}
              ariaLabel="Call department"
              departments={getHospitalDepartments(hospitalOptions, row.hospital)}
              value={row.department}
              onChange={value => onChange(rowIndex, "department", value)}
            />
          </Field>
        </div>
      </div>

      <div className="tscard-field-section">
        <span className="tscard-section-label">Time tracking</span>
        <div className="tscard-field-pair">
          <Field id={`tcall-${rowIndex}-start`} label="Start time">
            <TimeCell id={`tcall-${rowIndex}-start`} value={row.startTime} format={timeFormat} ariaLabel="Start time"
              onChange={v => onChange(rowIndex, "startTime", v)} />
          </Field>
          <Field id={`tcall-${rowIndex}-end`} label="End time">
            <TimeCell id={`tcall-${rowIndex}-end`} value={row.endTime} format={timeFormat} ariaLabel="End time"
              onChange={v => onChange(rowIndex, "endTime", v)} />
          </Field>
        </div>
        {Number(row.totalHours) > 0 && (
          <div className="tscard-compliance-row tscard-field-section" style={{paddingTop: "6px", borderTop: "none"}}>
            <span className="tscard-computed-inline">
              <span className="tscard-computed-label">Total</span>
              <strong className="tscard-computed-value">{row.totalHours} hrs</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CallBackShiftFields({ row, rowIndex, onChange, onTimeOutChange, timeFormat }) {
  return (
    <div className="today-shift-fields">

      <div className="tscard-field-section">
        <span className="tscard-section-label">Time tracking</span>
        <div className="tscard-time-grid">
          <Field id={`tcb-${rowIndex}-in1`} label="Call 1 In">
            <TimeCell id={`tcb-${rowIndex}-in1`} value={row.timeInOne} format={timeFormat} ariaLabel="Call 1 in"
              onChange={v => onChange(rowIndex, "timeInOne", v)} />
          </Field>
          <Field id={`tcb-${rowIndex}-out1`} label="Call 1 Out">
            <TimeCell id={`tcb-${rowIndex}-out1`} value={row.timeOutOne} format={timeFormat} ariaLabel="Call 1 out"
              onChange={v => onTimeOutChange(rowIndex, "timeOutOne", v)} />
          </Field>
          <Field id={`tcb-${rowIndex}-in2`} label="Call 2 In">
            <TimeCell id={`tcb-${rowIndex}-in2`} value={row.timeInTwo} format={timeFormat} ariaLabel="Call 2 in"
              onChange={v => onChange(rowIndex, "timeInTwo", v)} />
          </Field>
          <Field id={`tcb-${rowIndex}-out2`} label="Call 2 Out">
            <TimeCell id={`tcb-${rowIndex}-out2`} value={row.timeOutTwo} format={timeFormat} ariaLabel="Call 2 out"
              onChange={v => onTimeOutChange(rowIndex, "timeOutTwo", v)} />
          </Field>
        </div>
        {Number(row.totalHours) > 0 && (
          <div className="tscard-compliance-row" style={{paddingTop: "6px"}}>
            <span className="tscard-computed-inline">
              <span className="tscard-computed-label">Total</span>
              <strong className="tscard-computed-value">{row.totalHours} hrs</strong>
            </span>
          </div>
        )}
      </div>

      {/* Compliance — inline row */}
      <div className="tscard-field-section">
        <div className="tscard-compliance-checks">
          <label className="tscard-check-label" htmlFor={`tcb-${rowIndex}-missed`}>
            <input id={`tcb-${rowIndex}-missed`} type="checkbox" checked={row.missedBreakAfterSix || false}
              onChange={e => onChange(rowIndex, "missedBreakAfterSix", e.target.checked)} />
            <span>Missed break after 6 hours on a single callback</span>
          </label>
        </div>
      </div>

      {/* Notes — full width */}
      <div className="tscard-field-section">
        <label className="tscard-notes-label" htmlFor={`tcb-${rowIndex}-notes`}>Notes</label>
        <textarea
          id={`tcb-${rowIndex}-notes`}
          className="tscard-notes-textarea"
          rows="2"
          value={row.notes}
          placeholder="Add notes about this shift..."
          onChange={e => onChange(rowIndex, "notes", e.target.value)}
          onInput={e => { e.target.style.height = "auto"; e.target.style.height = `${e.target.scrollHeight}px`; }}
        />
      </div>
    </div>
  );
}

// ── Time tracking timeline (completed shift review) ────────────────────────

function TlSep() {
  return <span className="tscard-tl-sep" aria-hidden="true">·</span>;
}

function TlSeg({ label, value }) {
  return (
    <span className="tscard-tl-seg">
      <span className="tscard-tl-label">{label}</span>
      <span className="tscard-tl-val">{value}</span>
    </span>
  );
}

function TimeTrackingTimeline({ type, row, timeFormat }) {
  const f = v => fmtStatusTime(v, timeFormat);

  return (
    <div className="tscard-timeline" aria-label="Time tracking">
      {type === "regular" && (
        <>
          {row?.timeIn && <TlSeg label="In" value={f(row.timeIn)} />}
          {row?.breakStart && (
            <>
              <TlSep />
              <TlSeg
                label="Break"
                value={row.breakEnd ? `${f(row.breakStart)}–${f(row.breakEnd)}` : `${f(row.breakStart)} (open)`}
              />
            </>
          )}
          {row?.timeOut && (
            <>
              <TlSep />
              <TlSeg label="Out" value={f(row.timeOut)} />
            </>
          )}
        </>
      )}
      {type === "call" && (
        <>
          {row?.startTime && <TlSeg label="In" value={f(row.startTime)} />}
          {row?.endTime && (
            <>
              <TlSep />
              <TlSeg label="Out" value={f(row.endTime)} />
            </>
          )}
        </>
      )}
      {type === "callback" && (
        <>
          {row?.timeInOne && (
            <TlSeg
              label="Call 1"
              value={row.timeOutOne ? `${f(row.timeInOne)}–${f(row.timeOutOne)}` : f(row.timeInOne)}
            />
          )}
          {row?.timeInTwo && (
            <>
              <TlSep />
              <TlSeg
                label="Call 2"
                value={row.timeOutTwo ? `${f(row.timeInTwo)}–${f(row.timeOutTwo)}` : f(row.timeInTwo)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Elapsed timer hook ─────────────────────────────────────────────────────

function useElapsedTime(type, row, phase) {
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!ACTIVE_PHASES.has(phase)) return;
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, [phase]);

  if (!ACTIVE_PHASES.has(phase)) return null;

  const startStr =
    type === "regular" ? row?.timeIn
    : type === "call"  ? row?.startTime
    : (row?.timeInTwo ?? row?.timeInOne);

  if (!startStr) return null;

  const [h, m] = startStr.split(":").map(Number);
  const now = new Date();
  const startMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0).getTime();
  const diffMs = Date.now() - startMs;
  if (diffMs < 0) return null;

  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
}

// ── ShiftCard ──────────────────────────────────────────────────────────────

function DeleteConfirmRow({ onCancel, onConfirm }) {
  return (
    <div className="tscard-delete-confirm-row">
      <span className="tscard-delete-confirm-label">Delete this shift?</span>
      <button type="button" className="tscard-delete-confirm-cancel" onClick={onCancel}>Cancel</button>
      <button type="button" className="tscard-delete-confirm-ok" onClick={onConfirm}>Delete</button>
    </div>
  );
}

function ShiftCard({
  type, row, rowIndex, phase,
  isExpanded, onToggle,
  onClock, onClockIn, onClockOut, onChange, onTookBreakChange, onDelete, onCallBackTimeOutChange,
  hospitalOptions, timeFormat, callHospital, label
}) {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [clockInSaving, setClockInSaving] = React.useState(false);
  const [showSavedBanner, setShowSavedBanner] = React.useState(false);
  const [clockInError, setClockInError] = React.useState("");
  const [clockOutSaving, setClockOutSaving] = React.useState(false);
  const [showClockOutBanner, setShowClockOutBanner] = React.useState(false);
  const [clockOutError, setClockOutError] = React.useState("");
  const bannerTimerRef = React.useRef(null);
  const clockOutTimerRef = React.useRef(null);
  const bodyId = `today-shift-card-${type}-details`;

  React.useEffect(() => {
    return () => {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      if (clockOutTimerRef.current) clearTimeout(clockOutTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!isExpanded) {
      setIsEditMode(false);
      setConfirmingDelete(false);
      setShowSavedBanner(false);
      setClockInError("");
      setShowClockOutBanner(false);
      setClockOutError("");
    }
  }, [isExpanded]);

  const handleClockInClick = async () => {
    setClockInSaving(true);
    setClockInError("");
    try {
      await onClockIn();
      setShowSavedBanner(true);
      bannerTimerRef.current = setTimeout(() => setShowSavedBanner(false), 5500);
    } catch {
      setClockInError("Clock-in could not be saved. Please try again.");
    } finally {
      setClockInSaving(false);
    }
  };

  const handleClockOutClick = async () => {
    setClockOutSaving(true);
    setClockOutError("");
    try {
      await onClockOut();
      setShowClockOutBanner(true);
      clockOutTimerRef.current = setTimeout(() => setShowClockOutBanner(false), 5500);
    } catch {
      setClockOutError("Clock-out could not be saved. Please try again.");
    } finally {
      setClockOutSaving(false);
    }
  };

  const status = getShiftStatus(phase);
  const timeRange = getTimeRangeSummary(type, row, timeFormat);
  const isActive = ACTIVE_PHASES.has(phase);
  const clockStatusText = isActive ? getClockStatusText(type, phase, row, timeFormat) : null;
  const displayHospital = type === "callback" ? callHospital : row?.hospital;
  const canClockIn = !!displayHospital;
  const elapsed = useElapsedTime(type, row, phase);

  const editForm = row && (
    <>
      {type === "regular" && (
        <RegularShiftFields
          row={row} rowIndex={rowIndex} hospitalOptions={hospitalOptions}
          onChange={onChange} onTookBreakChange={onTookBreakChange}
          timeFormat={timeFormat}
        />
      )}
      {type === "call" && (
        <CallShiftFields
          row={row} rowIndex={rowIndex} hospitalOptions={hospitalOptions}
          onChange={onChange} timeFormat={timeFormat}
        />
      )}
      {type === "callback" && (
        <CallBackShiftFields
          row={row} rowIndex={rowIndex}
          onChange={onChange} onTimeOutChange={onCallBackTimeOutChange}
          timeFormat={timeFormat}
        />
      )}
    </>
  );

  return (
    <div
      className={[
        "today-shift-card",
        isActive ? "today-shift-card--active" : "",
        phase === "done" ? "today-shift-card--done" : "",
      ].filter(Boolean).join(" ")}
    >
      {/* Header */}
      <button
        type="button"
        className="today-shift-card-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={bodyId}
        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label ?? SHIFT_LABELS[type]} shift details`}
      >
        <span className="today-shift-card-chevron" aria-hidden="true">
          {isExpanded ? "▾" : "▸"}
        </span>
        <span className={`today-shift-type-badge today-shift-type-badge--${type}`}>
          {label ?? SHIFT_LABELS[type]}
        </span>
        {displayHospital && (
          <span className="today-shift-card-hospital">
            <span className="today-shift-card-hospital-sep" aria-hidden="true">·</span>
            {displayHospital}
          </span>
        )}
        <StatusPill status={status} />
        <span className="today-shift-card-spacer" />
        <span className="today-shift-card-meta">
          {timeRange && <span className="today-shift-card-time-range">{timeRange}</span>}
          {row?.totalHours && (
            <span className="today-shift-card-hours">{row.totalHours} hrs</span>
          )}
        </span>
      </button>

      {/* Body */}
      {isExpanded && (
        <div id={bodyId} className="today-shift-card-body">

          {/* ── EDIT MODE (all phases) ───────────────────────────────── */}
          {isEditMode && (
            <div className="tscard-done-view">
              {editForm}
              <div className="tscard-edit-footer">
                <button type="button" className="tscard-back-btn" onClick={() => setIsEditMode(false)}>
                  ← Back to summary
                </button>
              </div>
            </div>
          )}

          {/* ── OPERATIONAL MODE ────────────────────────────────────── */}
          {!isEditMode && (

            <>
              {/* IDLE: setup */}
              {phase === "idle" && (
                <div className="tscard-setup">
                  {type !== "callback" ? (
                    <>
                      <div className="tscard-setup-metadata">
                        <div className="tscard-setup-row">
                          <label className="tscard-setup-label" htmlFor={`tscard-${type}-hospital`}>
                            Hospital
                          </label>
                          <select
                            id={`tscard-${type}-hospital`}
                            value={row?.hospital ?? ""}
                            onChange={e => onChange(rowIndex, "hospital", e.target.value)}
                          >
                            <option value="">Select hospital…</option>
                            {hospitalOptions.map(h => (
                              <option key={h.hospitalId} value={h.name}>{h.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="tscard-setup-row">
                          <label className="tscard-setup-label" htmlFor={`tscard-${type}-dept`}>
                            Dept
                          </label>
                          <DepartmentSelect
                            id={`tscard-${type}-dept`}
                            ariaLabel={`${type} department`}
                            departments={getHospitalDepartments(hospitalOptions, row?.hospital ?? "")}
                            value={row?.department ?? ""}
                            onChange={value => onChange(rowIndex, "department", value)}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    canClockIn
                      ? <p className="tscard-setup-hint">Hospital: <strong>{callHospital}</strong></p>
                      : <p className="tscard-op-idle--warn">Ensure your On Call shift has a hospital selected.</p>
                  )}
                  <div className="tscard-action-row tscard-setup-actions">
                    <button
                      type="button"
                      className="tscard-btn-primary"
                      disabled={!canClockIn || clockInSaving}
                      onClick={handleClockInClick}
                    >
                      {clockInSaving ? "Saving…" : "Clock In"}
                    </button>
                    {confirmingDelete ? (
                      <DeleteConfirmRow onCancel={() => setConfirmingDelete(false)} onConfirm={onDelete} />
                    ) : (
                      <button type="button" className="tscard-delete-link" onClick={() => setConfirmingDelete(true)} aria-label="Delete shift">
                        Delete
                      </button>
                    )}
                  </div>
                  {clockInError && (
                    <p className="tscard-clockin-error" role="alert">{clockInError}</p>
                  )}
                </div>
              )}

              {/* ACTIVE: status + elapsed + workflow */}
              {isActive && (
                <div className="tscard-active-view">
                  <div className="tscard-op-main--active">
                    {showSavedBanner && (
                      <div className="tscard-saved-banner" role="status" aria-live="polite" aria-atomic="true">
                        <p className="tscard-saved-banner-title">✓ Clock-in saved</p>
                        <p className="tscard-saved-banner-body">
                          Started at {fmtStatusTime(
                            type === "regular" ? row?.timeIn : type === "call" ? row?.startTime : (row?.timeInTwo || row?.timeInOne),
                            timeFormat
                          )}
                        </p>
                      </div>
                    )}
                    {clockStatusText && (
                      <p className="tscard-clock-status">{clockStatusText}</p>
                    )}
                    {elapsed && (
                      <p className="tscard-elapsed">Elapsed: {elapsed}</p>
                    )}
                    <div className="tscard-action-row">
                      {type === "regular" && phase === "working" && (
                        <>
                          <button type="button" className="tscard-btn-secondary"
                            onClick={() => onClock("breakOut")}>Start Break</button>
                          <button type="button" className="tscard-btn-primary"
                            disabled={clockOutSaving}
                            onClick={handleClockOutClick}>
                            {clockOutSaving ? "Saving…" : "Clock Out"}
                          </button>
                        </>
                      )}
                      {type === "regular" && phase === "on-break" && (
                        <>
                          <button type="button" className="tscard-btn-primary"
                            onClick={() => onClock("breakIn")}>End Break</button>
                          <button type="button" className="tscard-btn-secondary"
                            disabled={clockOutSaving}
                            onClick={handleClockOutClick}>
                            {clockOutSaving ? "Saving…" : "Clock Out"}
                          </button>
                        </>
                      )}
                      {type === "regular" && phase === "after-break" && (
                        <button type="button" className="tscard-btn-primary"
                          disabled={clockOutSaving}
                          onClick={handleClockOutClick}>
                          {clockOutSaving ? "Saving…" : "Clock Out"}
                        </button>
                      )}
                      {(type === "call" || type === "callback") && (
                        <button type="button" className="tscard-btn-primary"
                          disabled={clockOutSaving}
                          onClick={handleClockOutClick}>
                          {clockOutSaving ? "Saving…" : "Clock Out"}
                        </button>
                      )}
                    </div>
                    {clockOutError && (
                      <p className="tscard-clockin-error" role="alert">{clockOutError}</p>
                    )}
                  </div>

                  <div className="tscard-card-footer">
                    {confirmingDelete ? (
                      <DeleteConfirmRow onCancel={() => setConfirmingDelete(false)} onConfirm={onDelete} />
                    ) : (
                      <>
                        <button type="button" className="tscard-edit-link" onClick={() => setIsEditMode(true)}>
                          Edit details
                        </button>
                        <button type="button" className="tscard-delete-link" onClick={() => setConfirmingDelete(true)} aria-label="Delete shift">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* DONE: compact read-only review */}
              {phase === "done" && (
                <div className="tscard-review">

                  {showClockOutBanner && (
                    <div className="tscard-saved-banner" role="status" aria-live="polite" aria-atomic="true">
                      <p className="tscard-saved-banner-title">✓ Clock-out saved</p>
                      <p className="tscard-saved-banner-body">
                        Ended at {fmtStatusTime(
                          type === "regular" ? row?.timeOut
                            : type === "call" ? row?.endTime
                            : (row?.timeOutTwo || row?.timeOutOne),
                          timeFormat
                        )}
                      </p>
                    </div>
                  )}

                  {/* Hospital/Dept — non-callback only */}
                  {type !== "callback" && (
                    <dl className="tscard-review-list">
                      <div className="tscard-review-row">
                        <dt>Hospital</dt>
                        <dd>{row?.hospital || <em className="tscard-muted">Not entered</em>}</dd>
                      </div>
                      <div className="tscard-review-row">
                        <dt>Dept</dt>
                        <dd>{row?.department || <em className="tscard-muted">Not entered</em>}</dd>
                      </div>
                    </dl>
                  )}

                  {/* Compact time tracking timeline */}
                  <TimeTrackingTimeline type={type} row={row} timeFormat={timeFormat} />

                  {/* Compliance — regular only */}
                  {type === "regular" && (
                    <div className="tscard-review-compliance">
                      <span>Missed break: <strong className="tscard-review-value">{row?.tookBreak ? "Yes" : "No"}</strong></span>
                      <span>Late break: <strong className="tscard-review-value">{row?.lateBreak ? "Yes" : "No"}</strong></span>
                      <span>Flexed: <strong className="tscard-review-value">{row?.flexed ? "Yes" : "No"}</strong></span>
                      <span>Voluntary Flex: <strong className="tscard-review-value">{row?.volFlex ? "Yes" : "No"}</strong></span>
                    </div>
                  )}

                  {/* Notes — regular and callback, omitted when empty */}
                  {(type === "regular" || type === "callback") && row?.notes && (
                    <div className="tscard-review-notes">
                      <p className="tscard-review-note-text">{row.notes}</p>
                    </div>
                  )}

                  <div className="tscard-review-footer">
                    {confirmingDelete ? (
                      <DeleteConfirmRow onCancel={() => setConfirmingDelete(false)} onConfirm={onDelete} />
                    ) : (
                      <>
                        <button type="button" className="tscard-edit-link" onClick={() => setIsEditMode(true)}>
                          Edit details
                        </button>
                        <button type="button" className="tscard-delete-link" onClick={() => setConfirmingDelete(true)} aria-label="Delete shift">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── AddShiftForm ───────────────────────────────────────────────────────────

function AddShiftForm({ availableTypes, hospitalOptions, onAdd, onCancel }) {
  const [selected, setSelected] = React.useState(availableTypes[0] ?? "regular");
  const [hospital, setHospital] = React.useState("");

  const isCallback = selected === "callback";
  const canCreate = isCallback || !!hospital;

  const handleTypeChange = (type) => {
    setSelected(type);
    setHospital("");
  };

  return (
    <div className="today-add-shift-form" role="group" aria-label="Choose shift type to add">
      <span className="today-add-shift-form-label">Shift type</span>
      <div className="today-add-shift-types" role="group" aria-label="Shift type options">
        {availableTypes.map(type => (
          <button
            key={type}
            type="button"
            className={`today-add-type-pill${selected === type ? " today-add-type-pill--selected" : ""}`}
            onClick={() => handleTypeChange(type)}
            aria-pressed={selected === type}
          >
            {SHIFT_LABELS[type]}
          </button>
        ))}
      </div>

      {isCallback ? (
        <p className="today-add-form-hint">Hospital is inherited from today's On Call shift.</p>
      ) : (
        <div className="today-add-form-field">
          <label className="today-add-form-label" htmlFor="today-add-hospital">Hospital</label>
          <select
            id="today-add-hospital"
            className="today-add-hospital-select"
            value={hospital}
            onChange={e => setHospital(e.target.value)}
          >
            <option value="">Select hospital</option>
            {hospitalOptions.map(h => (
              <option key={h.hospitalId} value={h.name}>{h.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="today-add-shift-actions">
        <button type="button" className="today-add-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="tscard-btn-primary"
          disabled={!canCreate}
          onClick={() => onAdd(selected, hospital)}
        >
          Create Shift
        </button>
      </div>
    </div>
  );
}

// ── TodaySection ───────────────────────────────────────────────────────────

export default function TodaySection({
  regularRows,
  callRows,
  callbackRows,
  onRegularRowChange,
  onRegularTookBreakChange,
  onCallRowChange,
  onCallBackRowChange,
  hospitalOptions = [],
  onClockAction,
  onClockInAction,
  onClockOutAction,
  onDeleteTodayShift,
  onAddRegularRow,
  getCallHospitalForDay
}) {
  const [expandedShift, setExpandedShift] = React.useState(null);
  const [addingShift, setAddingShift] = React.useState(false);
  const [manuallyAdded, setManuallyAdded] = React.useState([]);
  const { timeFormat, setTimeFormat } = useTimeFormat();

  const callPhase = React.useMemo(() => getCallPhase(callRows), [callRows]);
  const cbPhase   = React.useMemo(() => getCallBackPhase(callbackRows), [callbackRows]);

  const todayCall = React.useMemo(() => getTodayRow(callRows), [callRows]);
  const todayCb   = React.useMemo(() => getTodayRow(callbackRows), [callbackRows]);
  const callIdx   = React.useMemo(() => getTodayRowIndex(callRows), [callRows]);
  const cbIdx     = React.useMemo(() => getTodayRowIndex(callbackRows), [callbackRows]);

  const callHasData = React.useMemo(() => hasCallShiftData(todayCall), [todayCall]);
  const cbHasData   = React.useMemo(() => hasCallBackShiftData(todayCb), [todayCb]);

  // Base row index for today (first regular row matching today's day)
  const baseRegIdx = React.useMemo(() => getTodayRowIndex(regularRows), [regularRows]);

  // All regular rows for today that should be shown as cards
  const todayRegEntries = React.useMemo(() => {
    const d = DAYS[new Date().getDay()];
    return regularRows.reduce((acc, r, i) => {
      if (r.day !== d) return acc;
      const isBase = i === baseRegIdx;
      if (hasRegularShiftData(r) || (isBase && manuallyAdded.includes("regular"))) {
        return [...acc, { row: r, rowIndex: i }];
      }
      return acc;
    }, []);
  }, [regularRows, baseRegIdx, manuallyAdded]);

  const regularPhases = React.useMemo(
    () => todayRegEntries.map(e => getRegularPhaseForRow(e.row)),
    [todayRegEntries]
  );

  const visibleTypes = React.useMemo(() => {
    const s = new Set([
      ...(callHasData ? ["call"]     : []),
      ...(cbHasData   ? ["callback"] : []),
      ...manuallyAdded.filter(t => t === "call" || t === "callback")
    ]);
    return ["call", "callback"].filter(t => s.has(t));
  }, [callHasData, cbHasData, manuallyAdded]);

  // Regular is always available to add (multiple shifts for different hospitals)
  const availableToAdd = React.useMemo(
    () => ["regular", ...["call", "callback"].filter(t => !visibleTypes.includes(t))],
    [visibleTypes]
  );

  const activeCardKey = React.useMemo(() => {
    const activeEntry = todayRegEntries.find((e, i) => ACTIVE_PHASES.has(regularPhases[i]));
    if (activeEntry) return `regular_${activeEntry.rowIndex}`;
    if (callPhase === "working") return "call";
    if (cbPhase === "working")   return "callback";
    return null;
  }, [todayRegEntries, regularPhases, callPhase, cbPhase]);

  const prevActiveCardKeyRef = React.useRef(null);
  React.useEffect(() => {
    if (activeCardKey && activeCardKey !== prevActiveCardKeyRef.current) {
      setExpandedShift(activeCardKey);
      setAddingShift(false);
    }
    prevActiveCardKeyRef.current = activeCardKey;
  }, [activeCardKey]);

  // Auto-expand when a new regular card is added
  const prevRegCountRef = React.useRef(null);
  React.useEffect(() => {
    const curr = todayRegEntries.length;
    const prev = prevRegCountRef.current;
    prevRegCountRef.current = curr;
    if (prev !== null && curr > prev) {
      const last = todayRegEntries[curr - 1];
      setExpandedShift(`regular_${last.rowIndex}`);
    }
  }, [todayRegEntries]);

  const orderedTypes = React.useMemo(() => {
    const activeNonReg = callPhase === "working" ? "call" : cbPhase === "working" ? "callback" : null;
    return [
      ...(activeNonReg ? [activeNonReg] : []),
      ...visibleTypes.filter(t => t !== activeNonReg)
    ];
  }, [visibleTypes, callPhase, cbPhase]);

  const handleRegularClock = React.useCallback((fieldType, rowIndex) => {
    const result = clockIntoTodayRow(regularRows, onRegularRowChange, fieldType, {
      dateField: "date",
      sequence: [
        { button: "clockIn",  field: "timeIn"     },
        { button: "breakOut", field: "breakStart" },
        { button: "breakIn",  field: "breakEnd"   },
        { button: "clockOut", field: "timeOut"    }
      ],
      skipToButtons: ["clockOut"]
    }, rowIndex);
    if (result) {
      const nextRow  = { ...result.row, totalHours: calculateRegularTotalHours(result.row, hospitalOptions) };
      const nextRows = regularRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      onClockAction?.({ regularRows: nextRows });
    }
  }, [regularRows, onRegularRowChange, onClockAction, hospitalOptions]);

  const handleCallClock = React.useCallback((fieldType) => {
    const result = clockIntoTodayRow(callRows, onCallRowChange, fieldType, {
      dateField: "date",
      inFields:  ["startTime"],
      outFields: ["endTime"]
    });
    if (result) {
      const nextRow = { ...result.row };
      if (result.field === "endTime") nextRow.totalHours = calculateCallTotalHours(nextRow, hospitalOptions);
      const nextRows = callRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      onClockAction?.({ callRows: nextRows });
    }
  }, [callRows, onCallRowChange, onClockAction, hospitalOptions]);

  const handleCbClock = React.useCallback((fieldType) => {
    const result = clockIntoTodayRow(callbackRows, onCallBackRowChange, fieldType, {
      inFields:  ["timeInOne",  "timeInTwo"],
      outFields: ["timeOutOne", "timeOutTwo"]
    });
    if (result) {
      const nextRow = { ...result.row };
      if (result.field === "timeOutOne" || result.field === "timeOutTwo") {
        const hospital = getCallHospitalForDay?.(nextRow.day) ?? "";
        nextRow.totalHours = calculateCallBackTotalHours(nextRow, hospital, hospitalOptions);
      }
      const nextRows = callbackRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      onClockAction?.({ callbackRows: nextRows });
    }
  }, [callbackRows, onCallBackRowChange, onClockAction, getCallHospitalForDay, hospitalOptions]);

  const handleRegularClockIn = React.useCallback(async (rowIndex) => {
    const result = clockIntoTodayRow(regularRows, onRegularRowChange, "clockIn", {
      dateField: "date",
      sequence: [
        { button: "clockIn",  field: "timeIn"     },
        { button: "breakOut", field: "breakStart" },
        { button: "breakIn",  field: "breakEnd"   },
        { button: "clockOut", field: "timeOut"    }
      ],
      skipToButtons: ["clockOut"]
    }, rowIndex);
    if (result) {
      const nextRow  = { ...result.row, totalHours: calculateRegularTotalHours(result.row, hospitalOptions) };
      const nextRows = regularRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      await onClockInAction?.({ regularRows: nextRows });
    }
  }, [regularRows, onRegularRowChange, onClockInAction, hospitalOptions]);

  const handleCallClockIn = React.useCallback(async () => {
    const result = clockIntoTodayRow(callRows, onCallRowChange, "in", {
      dateField: "date",
      inFields:  ["startTime"],
      outFields: ["endTime"]
    });
    if (result) {
      const nextRows = callRows.map((r, i) => i === result.rowIndex ? result.row : r);
      await onClockInAction?.({ callRows: nextRows });
    }
  }, [callRows, onCallRowChange, onClockInAction]);

  const handleCbClockIn = React.useCallback(async () => {
    const result = clockIntoTodayRow(callbackRows, onCallBackRowChange, "in", {
      inFields:  ["timeInOne",  "timeInTwo"],
      outFields: ["timeOutOne", "timeOutTwo"]
    });
    if (result) {
      const nextRows = callbackRows.map((r, i) => i === result.rowIndex ? result.row : r);
      await onClockInAction?.({ callbackRows: nextRows });
    }
  }, [callbackRows, onCallBackRowChange, onClockInAction]);

  const handleRegularClockOut = React.useCallback(async (rowIndex) => {
    const result = clockIntoTodayRow(regularRows, onRegularRowChange, "clockOut", {
      dateField: "date",
      sequence: [
        { button: "clockIn",  field: "timeIn"     },
        { button: "breakOut", field: "breakStart" },
        { button: "breakIn",  field: "breakEnd"   },
        { button: "clockOut", field: "timeOut"    }
      ],
      skipToButtons: ["clockOut"]
    }, rowIndex);
    if (result) {
      const nextRow  = { ...result.row, totalHours: calculateRegularTotalHours(result.row, hospitalOptions) };
      const nextRows = regularRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      await onClockOutAction?.({ regularRows: nextRows });
    }
  }, [regularRows, onRegularRowChange, onClockOutAction, hospitalOptions]);

  const handleCallClockOut = React.useCallback(async () => {
    const result = clockIntoTodayRow(callRows, onCallRowChange, "out", {
      dateField: "date",
      inFields:  ["startTime"],
      outFields: ["endTime"]
    });
    if (result) {
      const nextRow = { ...result.row };
      if (result.field === "endTime") nextRow.totalHours = calculateCallTotalHours(nextRow, hospitalOptions);
      const nextRows = callRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      await onClockOutAction?.({ callRows: nextRows });
    }
  }, [callRows, onCallRowChange, onClockOutAction, hospitalOptions]);

  const handleCbClockOut = React.useCallback(async () => {
    const result = clockIntoTodayRow(callbackRows, onCallBackRowChange, "out", {
      inFields:  ["timeInOne",  "timeInTwo"],
      outFields: ["timeOutOne", "timeOutTwo"]
    });
    if (result) {
      const nextRow = { ...result.row };
      if (result.field === "timeOutOne" || result.field === "timeOutTwo") {
        const hospital = getCallHospitalForDay?.(nextRow.day) ?? "";
        nextRow.totalHours = calculateCallBackTotalHours(nextRow, hospital, hospitalOptions);
      }
      const nextRows = callbackRows.map((r, i) => i === result.rowIndex ? nextRow : r);
      await onClockOutAction?.({ callbackRows: nextRows });
    }
  }, [callbackRows, onCallBackRowChange, onClockOutAction, getCallHospitalForDay, hospitalOptions]);

  const handleCbTimeOutChange = React.useCallback((rowIndex, field, value) => {
    onCallBackRowChange(rowIndex, field, value);
  }, [onCallBackRowChange]);

  const handleToggle = (cardKey) => {
    setExpandedShift(prev => prev === cardKey ? null : cardKey);
    setAddingShift(false);
  };

  const handleDeleteRegular = (rowIndex) => {
    onDeleteTodayShift?.("regular", rowIndex);
    if (rowIndex === baseRegIdx) {
      setManuallyAdded(prev => prev.filter(t => t !== "regular"));
    }
    if (expandedShift === `regular_${rowIndex}`) setExpandedShift(null);
  };

  const handleDelete = (type) => {
    const hasData = type === "call" ? callHasData : cbHasData;
    if (hasData) onDeleteTodayShift?.(type);
    setManuallyAdded(prev => prev.filter(t => t !== type));
    if (expandedShift === type) setExpandedShift(null);
  };

  const handleAddShift = (type, hospital) => {
    if (type === "regular") {
      if (todayRegEntries.length === 0) {
        if (hospital && baseRegIdx >= 0) onRegularRowChange(baseRegIdx, "hospital", hospital);
        setManuallyAdded(prev => prev.includes("regular") ? prev : [...prev, "regular"]);
        setExpandedShift(`regular_${baseRegIdx >= 0 ? baseRegIdx : 0}`);
      } else {
        onAddRegularRow?.(hospital);
      }
      setAddingShift(false);
      return;
    }
    if (hospital) {
      if (type === "call" && callIdx >= 0) onCallRowChange(callIdx, "hospital", hospital);
    }
    setManuallyAdded(prev => prev.includes(type) ? prev : [...prev, type]);
    setExpandedShift(type);
    setAddingShift(false);
  };

  const getPhase    = t => t === "call" ? callPhase : cbPhase;
  const getRow      = t => t === "call" ? todayCall  : todayCb;
  const getIdx      = t => t === "call" ? callIdx    : cbIdx;
  const getClock    = t => t === "call" ? handleCallClock : handleCbClock;
  const getOnChange = t => t === "call" ? onCallRowChange : onCallBackRowChange;

  const todayLabel = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric"
  });

  return (
    <section className="sheet-section today-section" aria-label="Today's shifts">
      <div className="today-section-header">
        <h2 className="today-section-title section-title">Today</h2>
        <span className="today-section-date">{todayLabel}</span>
      </div>

      <div className="today-format-preference">
        <TimeFormatToggle value={timeFormat} onChange={setTimeFormat} />
        <p className="today-format-hint">We'll remember this preference for next time.</p>
      </div>

      {(todayRegEntries.length > 0 || orderedTypes.length > 0) && (
        <div className="today-shift-cards">
          {todayRegEntries.map((entry, i) => (
            <ShiftCard
              key={`regular_${entry.rowIndex}`}
              type="regular"
              label={`Shift ${i + 1}`}
              row={entry.row}
              rowIndex={entry.rowIndex}
              phase={regularPhases[i]}
              isExpanded={expandedShift === `regular_${entry.rowIndex}`}
              onToggle={() => handleToggle(`regular_${entry.rowIndex}`)}
              onClock={(fieldType) => handleRegularClock(fieldType, entry.rowIndex)}
              onClockIn={() => handleRegularClockIn(entry.rowIndex)}
              onClockOut={() => handleRegularClockOut(entry.rowIndex)}
              onChange={onRegularRowChange}
              onTookBreakChange={onRegularTookBreakChange}
              onDelete={() => handleDeleteRegular(entry.rowIndex)}
              hospitalOptions={hospitalOptions}
              timeFormat={timeFormat}
            />
          ))}
          {orderedTypes.map(type => (
            <ShiftCard
              key={type}
              type={type}
              row={getRow(type)}
              rowIndex={getIdx(type)}
              phase={getPhase(type)}
              isExpanded={expandedShift === type}
              onToggle={() => handleToggle(type)}
              onClock={getClock(type)}
              onClockIn={type === "call" ? handleCallClockIn : handleCbClockIn}
              onClockOut={type === "call" ? handleCallClockOut : handleCbClockOut}
              onChange={getOnChange(type)}
              onTookBreakChange={onRegularTookBreakChange}
              onDelete={() => handleDelete(type)}
              onCallBackTimeOutChange={handleCbTimeOutChange}
              hospitalOptions={hospitalOptions}
              timeFormat={timeFormat}
              callHospital={type === "callback"
                ? (getCallHospitalForDay?.(DAYS[new Date().getDay()]) ?? "")
                : undefined}
            />
          ))}
        </div>
      )}

      {availableToAdd.length > 0 && (
        <div className="today-add-shift-area">
          {addingShift ? (
            <AddShiftForm
              availableTypes={availableToAdd}
              hospitalOptions={hospitalOptions}
              onAdd={handleAddShift}
              onCancel={() => setAddingShift(false)}
            />
          ) : (
            <button
              type="button"
              className="today-add-shift-btn"
              onClick={() => setAddingShift(true)}
            >
              <span aria-hidden="true">+</span> Add shift
            </button>
          )}
        </div>
      )}

      {todayRegEntries.length === 0 && orderedTypes.length === 0 && !addingShift && (
        <p className="today-empty-state">No shifts added for today yet.</p>
      )}
    </section>
  );
}
